/**
 * Vendor portal — Settings.
 * Shop profile (name, bio, logo, banner), policies (returns, shipping),
 * support email, community pick, and payout connection.
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useVendor } from "@/lib/store/useVendor";
import { supabase } from "@/integrations/supabase/client";
import { APPS, type AppId } from "@/lib/apps";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, ExternalLink, Sparkles, Upload, X } from "lucide-react";

const VendorSettings = () => {
  const { user } = useAuth();
  const { vendor, refetch, isSandbox } = useVendor();
  const { toast } = useToast();

  const [displayName, setDisplayName] = useState("");
  const [bio, setBio] = useState("");
  const [supportEmail, setSupportEmail] = useState("");
  const [policyReturns, setPolicyReturns] = useState("");
  const [policyShipping, setPolicyShipping] = useState("");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [bannerUrl, setBannerUrl] = useState<string | null>(null);
  const [appId, setAppId] = useState<AppId | "">("");
  const [saving, setSaving] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [uploadingKind, setUploadingKind] = useState<"logo" | "banner" | null>(null);

  useEffect(() => {
    if (!vendor) return;
    setDisplayName(vendor.display_name ?? "");
    setBio(vendor.bio ?? "");
    setSupportEmail(vendor.support_email ?? "");
    setPolicyReturns(vendor.policy_returns ?? "");
    setPolicyShipping(vendor.policy_shipping ?? "");
    setLogoUrl(vendor.logo_url);
    setBannerUrl(vendor.banner_url);
    setAppId((vendor.app_id ?? "") as AppId | "");
  }, [vendor]);

  const upload = async (kind: "logo" | "banner", file: File) => {
    if (!user) return;
    setUploadingKind(kind);
    const ext = file.name.split(".").pop() ?? "jpg";
    const path = `${user.id}/shop/${kind}-${Date.now()}.${ext}`;
    const { error } = await supabase.storage
      .from("store-images")
      .upload(path, file, { contentType: file.type, upsert: true });
    if (error) {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
      setUploadingKind(null);
      return;
    }
    const { data } = supabase.storage.from("store-images").getPublicUrl(path);
    if (kind === "logo") setLogoUrl(data.publicUrl);
    else setBannerUrl(data.publicUrl);
    setUploadingKind(null);
  };

  const save = async () => {
    if (!vendor) return;
    setSaving(true);
    const { error } = await supabase
      .from("store_vendors")
      .update({
        display_name: displayName,
        bio: bio || null,
        support_email: supportEmail || null,
        policy_returns: policyReturns || null,
        policy_shipping: policyShipping || null,
        logo_url: logoUrl,
        banner_url: bannerUrl,
        app_id: appId || null,
      })
      .eq("id", vendor.id);
    setSaving(false);
    if (error) {
      toast({ title: "Save failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Settings saved" });
    void refetch();
  };

  const launchStripe = async () => {
    setStripeLoading(true);
    const origin = window.location.origin;
    const { data, error } = await supabase.functions.invoke("store-vendor-onboard", {
      body: {
        return_url: `${origin}/store/vendor/settings?stripe=done`,
        refresh_url: `${origin}/store/vendor/settings?stripe=refresh`,
      },
    });
    setStripeLoading(false);
    if (error || !data?.url) {
      toast({ title: "Error", description: error?.message ?? "Could not start onboarding.", variant: "destructive" });
      return;
    }
    window.location.href = data.url;
  };

  if (!vendor) return null;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Settings</h2>
        <p className="text-sm text-muted-foreground">
          Edit your storefront and manage payment connection.
        </p>
      </div>

      <Card className="p-5 space-y-4">
        <h3 className="font-semibold">Storefront</h3>
        <div className="space-y-2">
          <Label>Shop name</Label>
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          <p className="text-xs text-muted-foreground">URL: /store/vendor/{vendor.slug}</p>
        </div>
        <div className="space-y-2">
          <Label>Bio</Label>
          <Textarea rows={4} value={bio} onChange={(e) => setBio(e.target.value)} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ImageField
            label="Logo (square)"
            url={logoUrl}
            onUpload={(f) => upload("logo", f)}
            onRemove={() => setLogoUrl(null)}
            uploading={uploadingKind === "logo"}
            aspect="aspect-square w-32"
          />
          <ImageField
            label="Banner (wide)"
            url={bannerUrl}
            onUpload={(f) => upload("banner", f)}
            onRemove={() => setBannerUrl(null)}
            uploading={uploadingKind === "banner"}
            aspect="aspect-[3/1] w-full max-w-md"
          />
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <h3 className="font-semibold">Community</h3>
        <p className="text-sm text-muted-foreground">
          Which community does your brand belong to? Members of this app see your products
          highlighted.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {APPS.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => setAppId(a.id)}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors text-left ${
                appId === a.id
                  ? "border-primary bg-primary/10 text-foreground"
                  : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
              }`}
            >
              <img src={a.icon} alt="" className="h-5 w-5 rounded-sm shrink-0" />
              {a.name}
            </button>
          ))}
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <h3 className="font-semibold">Policies</h3>
        <div className="space-y-2">
          <Label>Support email</Label>
          <Input
            type="email"
            value={supportEmail}
            onChange={(e) => setSupportEmail(e.target.value)}
            placeholder="support@yourshop.com"
          />
        </div>
        <div className="space-y-2">
          <Label>Returns policy</Label>
          <Textarea
            rows={4}
            value={policyReturns}
            onChange={(e) => setPolicyReturns(e.target.value)}
            placeholder="Describe your return / refund policy. Buyers always have the 14-day Wavlynq Store window in addition to your terms."
          />
        </div>
        <div className="space-y-2">
          <Label>Shipping policy</Label>
          <Textarea
            rows={4}
            value={policyShipping}
            onChange={(e) => setPolicyShipping(e.target.value)}
            placeholder="Describe shipping origins, processing times, carriers, and international availability."
          />
        </div>
      </Card>

      <Card className="p-5 space-y-3">
        <h3 className="font-semibold">Payments</h3>
        {isSandbox ? (
          <div className="flex items-center gap-3 text-sm">
            <Sparkles className="h-5 w-5 text-primary" />
            <div>
              <p className="font-medium">Sandbox vendor</p>
              <p className="text-muted-foreground">
                Stripe Connect is bypassed in sandbox mode. No real payouts will be made.
              </p>
            </div>
          </div>
        ) : vendor.stripe_charges_enabled ? (
          <div className="flex items-center gap-3 text-sm">
            <CheckCircle className="h-5 w-5 text-primary" />
            <p>Stripe is connected. Manage payouts from the Payouts page.</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Connect your Stripe account to start receiving payouts. We handle PCI compliance and
              transfers.
            </p>
            <Button onClick={launchStripe} disabled={stripeLoading}>
              {stripeLoading
                ? "Redirecting…"
                : vendor.stripe_account_id
                  ? "Continue Stripe setup"
                  : "Connect with Stripe"}
              <ExternalLink className="ml-2 h-4 w-4" />
            </Button>
          </>
        )}
      </Card>

      <div className="sticky bottom-4 flex justify-end">
        <Button onClick={save} disabled={saving} size="lg" className="shadow-lg">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <Card className="p-5 text-xs text-muted-foreground space-y-1">
        <p>Vendor ID: <code>{vendor.id}</code></p>
        <p>Status: <Badge variant="secondary" className="capitalize">{vendor.status.replace(/_/g, " ")}</Badge></p>
        <p>Created: {new Date(vendor.created_at).toLocaleDateString()}</p>
      </Card>
    </div>
  );
};

const ImageField = ({
  label,
  url,
  onUpload,
  onRemove,
  uploading,
  aspect,
}: {
  label: string;
  url: string | null;
  onUpload: (f: File) => void;
  onRemove: () => void;
  uploading: boolean;
  aspect: string;
}) => (
  <div className="space-y-2">
    <Label>{label}</Label>
    {url ? (
      <div className={`relative ${aspect} rounded-md overflow-hidden bg-muted group`}>
        <img src={url} alt="" className="h-full w-full object-cover" />
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-1 right-1 bg-background/80 rounded p-1 opacity-0 group-hover:opacity-100 transition"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    ) : (
      <label
        className={`cursor-pointer flex flex-col items-center justify-center ${aspect} rounded-md border border-dashed text-muted-foreground hover:bg-muted/30 transition-colors text-xs`}
      >
        <input
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => e.target.files?.[0] && onUpload(e.target.files[0])}
          disabled={uploading}
        />
        <Upload className="h-5 w-5 mb-1" />
        {uploading ? "Uploading…" : "Click to upload"}
      </label>
    )}
  </div>
);

export default VendorSettings;
